import axios from 'axios';
import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Location } from '../utils/GeocoderUtility';

interface CityLocationContextType {
  cityLocation: string;
  setCityLocation: (cityLocation: string) => void;
  addChangeListener: (listener: (newLocation: string) => void) => void;
  removeChangeListener: (listener: (newLocation: string) => void) => void;
}

const CityLocationContext = createContext<CityLocationContextType | undefined>(undefined);

export const CityLocationProvider = ({ children }: { children: ReactNode }) => {
  const [, setLocation] = useState<Location | null>(null);
  const [cityLocation, setCityLocation] = useState<string>('');
  const [listeners] = useState<((newLocation: string) => void)[]>([]);
  const isFirstRun = useRef(true);

  const notifyListeners = useCallback((newLocation: string) => {
    listeners.forEach(listener => listener(newLocation));
  }, [listeners]);

  const updateCityLocation = useCallback((newLocation: string) => {
    if (cityLocation === newLocation) return;
    console.log(`CityLocationProvider: changed city from ${cityLocation} to ${newLocation}`);

    setCityLocation(newLocation);
    notifyListeners(newLocation);
  }, [notifyListeners, cityLocation]);

  const addChangeListener = useCallback((listener: (newLocation: string) => void) => {
    listeners.push(listener);
  }, [listeners]);

  const removeChangeListener = useCallback((listener: (newLocation: string) => void) => {
    const index = listeners.indexOf(listener);
    if (index > -1) {
      listeners.splice(index, 1);
    }
  }, [listeners]);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            setLocation([latitude, longitude]);

            try {
              const response = await axios.get(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
              );

              if (response.data?.address) {
                const { village, state } = response.data.address;
                if (village && state) {
                  setCityLocation(`${village}, ${state}`);
                  // TODO Tell PlayButton to use this location
                }
              }
            } catch (error) {
              console.error('Error getting city:', error);
            }
          },
          (error) => {
            console.error(error);
            setLocation(null);
          }
        );
      }
    }
  }, []);


  return (
    <CityLocationContext.Provider
      value={{
        cityLocation,
        setCityLocation: updateCityLocation,
        addChangeListener,
        removeChangeListener
      }}
    >
      {children}
    </CityLocationContext.Provider>
  );
};

export const useCityLocation = () => {
  const context = useContext(CityLocationContext);
  if (!context) {
    throw new Error('useCityLocation must be used within a CityLocationProvider');
  }
  return context;
};